import React, { useEffect, useState } from "react";
import { HashLink } from "./HashLink";
import { useLang } from "./LangContext";
import { useTenant } from "./TenantContext";
import axios from "axios";
import { ArrowUpRight, Building2, User, Phone, Smartphone, Mail, MapPin, Landmark } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function EventsPreview() {
    const { t } = useLang();
    const { slug } = useTenant();
    const [events, setEvents] = useState([]);
    useEffect(() => {
        axios.get(`${API}/events`, { params: { borgo: slug } }).then((r) => setEvents(r.data)).catch(() => {});
    }, [slug]);

    return (
        <section id="eventi" data-testid="eventi-section" className="py-24 md:py-32 px-6 lg:px-10" style={{ background: "linear-gradient(180deg, #fef8ef 0%, #fbf8f1 100%)" }}>
            <div className="max-w-7xl mx-auto">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-14 gap-4">
                    <div>
                        <div className="text-xs font-semibold tracking-[0.3em] uppercase text-amber-700 mb-3">
                            NEWS · EVENTI
                        </div>
                        <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-semibold text-stone-900">
                            {t.eventi_title}
                        </h2>
                    </div>
                    <HashLink
                        to="/eventi"
                        className="inline-flex items-center gap-2 text-emerald-700 font-semibold hover:text-emerald-800 transition-colors group"
                        data-testid="eventi-read-more"
                    >
                        {t.eventi_read_more}
                        <ArrowUpRight size={18} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                    </HashLink>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
                    {events.map((ev, i) => (
                        <HashLink
                            to="/eventi"
                            key={ev.id}
                            className="card-hover bg-white border border-stone-200 rounded-2xl overflow-hidden block"
                            data-testid={`event-card-${i}`}
                        >
                            <div className="aspect-[16/10] overflow-hidden relative">
                                <img
                                    src={ev.image}
                                    alt={ev.title}
                                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                                />
                                <span className="absolute top-3 left-3 bg-white/95 backdrop-blur text-xs font-semibold px-3 py-1 rounded-full text-emerald-700 uppercase tracking-wider">
                                    {ev.category}
                                </span>
                            </div>
                            <div className="p-6">
                                <div className="text-xs text-stone-500 font-semibold mb-2">{ev.date_label}</div>
                                <h3 className="font-serif text-2xl font-semibold text-stone-900 mb-2 line-clamp-2">{ev.title}</h3>
                                <p className="text-stone-600 text-sm line-clamp-3 leading-relaxed">{ev.excerpt}</p>
                            </div>
                        </HashLink>
                    ))}
                </div>
            </div>
        </section>
    );
}

export function ContactSection() {
    const { t, lang } = useLang();
    const { slug } = useTenant();
    const [form, setForm] = useState({ name: "", email: "", phone: "", message: "", consent: false });
    const [status, setStatus] = useState({ loading: false, ok: false, error: "" });
    const [tenantInfo, setTenantInfo] = useState(null);

    useEffect(() => {
        if (!slug) return;
        axios.get(`${API}/tenants/public/${slug}`).then((r) => setTenantInfo(r.data)).catch(() => {});
    }, [slug]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.consent) {
            setStatus({ loading: false, ok: false, error: lang === "it" ? "Il consenso è obbligatorio" : "Consent is required" });
            return;
        }
        setStatus({ loading: true, ok: false, error: "" });
        try {
            await axios.post(`${API}/contact`, form, { params: { borgo: slug } });
            setStatus({ loading: false, ok: true, error: "" });
            setForm({ name: "", email: "", phone: "", message: "", consent: false });
        } catch (err) {
            setStatus({ loading: false, ok: false, error: t.contact_error });
        }
    };

    const c = tenantInfo?.contact || {};
    const hasEnteInfo = !!(c.ente || c.referente || c.tel_fisso || c.cellulare || c.pec || c.address || c.email || (c.lat && c.lng));
    const mapsUrl = (c.lat && c.lng)
        ? `https://www.google.com/maps/search/?api=1&query=${c.lat},${c.lng}`
        : (c.address ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(c.address)}` : "");

    return (
        <section id="contatti" data-testid="contact-section" className="py-24 md:py-32 px-6 lg:px-10 bg-stone-50">
            <div className="max-w-4xl mx-auto text-center mb-14">
                <div className="text-xs font-semibold tracking-[0.3em] uppercase text-emerald-700 mb-3">
                    {lang === "it" ? "SCRIVICI" : "GET IN TOUCH"}
                </div>
                <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-semibold text-stone-900">{t.contact_title}</h2>
            </div>

            {/* Contatti Ente pubblici */}
            {hasEnteInfo && (
                <div className="max-w-3xl mx-auto mb-8" data-testid="contact-ente-card">
                    <div className="bg-white border border-emerald-200 rounded-3xl p-6 md:p-8 shadow-sm">
                        <div className="flex items-center gap-2 mb-4">
                            <Landmark size={16} className="text-emerald-700" />
                            <span className="text-xs font-bold uppercase tracking-[0.25em] text-emerald-800">
                                {lang === "it" ? "Contatti dell'ente" : "Institutional contacts"}
                            </span>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 gap-x-8">
                            {c.ente && (
                                <div className="flex items-start gap-3" data-testid="contact-ente-name">
                                    <Building2 size={16} className="mt-0.5 text-emerald-700 shrink-0" />
                                    <div>
                                        <div className="text-[10px] font-semibold uppercase tracking-widest text-stone-500">Ente</div>
                                        <div className="text-sm font-semibold text-stone-900">{c.ente}</div>
                                    </div>
                                </div>
                            )}
                            {c.referente && (
                                <div className="flex items-start gap-3" data-testid="contact-ente-referente">
                                    <User size={16} className="mt-0.5 text-emerald-700 shrink-0" />
                                    <div>
                                        <div className="text-[10px] font-semibold uppercase tracking-widest text-stone-500">Referente</div>
                                        <div className="text-sm text-stone-800">{c.referente}</div>
                                    </div>
                                </div>
                            )}
                            {c.tel_fisso && (
                                <div className="flex items-start gap-3" data-testid="contact-ente-tel">
                                    <Phone size={16} className="mt-0.5 text-emerald-700 shrink-0" />
                                    <div>
                                        <div className="text-[10px] font-semibold uppercase tracking-widest text-stone-500">{lang === "it" ? "Telefono" : "Phone"}</div>
                                        <a href={`tel:${c.tel_fisso}`} className="text-sm text-stone-800 hover:text-emerald-700">{c.tel_fisso}</a>
                                    </div>
                                </div>
                            )}
                            {c.cellulare && (
                                <div className="flex items-start gap-3" data-testid="contact-ente-cell">
                                    <Smartphone size={16} className="mt-0.5 text-emerald-700 shrink-0" />
                                    <div>
                                        <div className="text-[10px] font-semibold uppercase tracking-widest text-stone-500">{lang === "it" ? "Cellulare" : "Mobile"}</div>
                                        <a href={`tel:${c.cellulare}`} className="text-sm text-stone-800 hover:text-emerald-700">{c.cellulare}</a>
                                    </div>
                                </div>
                            )}
                            {c.email && (
                                <div className="flex items-start gap-3" data-testid="contact-ente-email">
                                    <Mail size={16} className="mt-0.5 text-emerald-700 shrink-0" />
                                    <div>
                                        <div className="text-[10px] font-semibold uppercase tracking-widest text-stone-500">Email</div>
                                        <a href={`mailto:${c.email}`} className="text-sm text-stone-800 hover:text-emerald-700 break-all">{c.email}</a>
                                    </div>
                                </div>
                            )}
                            {c.pec && (
                                <div className="flex items-start gap-3" data-testid="contact-ente-pec">
                                    <Mail size={16} className="mt-0.5 text-emerald-700 shrink-0" />
                                    <div>
                                        <div className="text-[10px] font-semibold uppercase tracking-widest text-stone-500">PEC</div>
                                        <a href={`mailto:${c.pec}`} className="text-sm text-stone-800 hover:text-emerald-700 break-all">{c.pec}</a>
                                    </div>
                                </div>
                            )}
                            {(c.address || c.cap) && (
                                <div className="flex items-start gap-3 md:col-span-2" data-testid="contact-ente-address">
                                    <MapPin size={16} className="mt-0.5 text-emerald-700 shrink-0" />
                                    <div className="flex-1">
                                        <div className="text-[10px] font-semibold uppercase tracking-widest text-stone-500">{lang === "it" ? "Indirizzo" : "Address"}</div>
                                        <div className="text-sm text-stone-800">
                                            {c.address}{c.cap && !String(c.address || "").includes(c.cap) ? ` — ${c.cap}` : ""}
                                        </div>
                                        {mapsUrl && (
                                            <a href={mapsUrl} target="_blank" rel="noreferrer" className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 hover:text-emerald-900" data-testid="contact-ente-maps-link">
                                                <MapPin size={11} /> {lang === "it" ? "Apri in Google Maps" : "Open in Google Maps"}
                                            </a>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
            <form
                onSubmit={handleSubmit}
                className="max-w-3xl mx-auto bg-white border border-stone-200 rounded-3xl p-8 md:p-12 shadow-sm"
                data-testid="contact-form"
            >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                        <label className="text-sm font-medium text-stone-700 mb-2 block">{t.contact_name}</label>
                        <input
                            type="text"
                            required
                            value={form.name}
                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                            className="input-elegant"
                            data-testid="contact-name-input"
                        />
                    </div>
                    <div>
                        <label className="text-sm font-medium text-stone-700 mb-2 block">{t.contact_email}</label>
                        <input
                            type="email"
                            required
                            value={form.email}
                            onChange={(e) => setForm({ ...form, email: e.target.value })}
                            className="input-elegant"
                            data-testid="contact-email-input"
                        />
                    </div>
                </div>
                <div className="mt-5">
                    <label className="text-sm font-medium text-stone-700 mb-2 block">{t.contact_phone}</label>
                    <input
                        type="tel"
                        value={form.phone}
                        onChange={(e) => setForm({ ...form, phone: e.target.value })}
                        className="input-elegant"
                        data-testid="contact-phone-input"
                    />
                </div>
                <div className="mt-5">
                    <label className="text-sm font-medium text-stone-700 mb-2 block">{t.contact_message}</label>
                    <textarea
                        required
                        rows={5}
                        value={form.message}
                        onChange={(e) => setForm({ ...form, message: e.target.value })}
                        className="input-elegant resize-none"
                        data-testid="contact-message-input"
                    />
                </div>
                <label className="mt-5 flex items-start gap-3 cursor-pointer select-none">
                    <input
                        type="checkbox"
                        checked={form.consent}
                        onChange={(e) => setForm({ ...form, consent: e.target.checked })}
                        className="mt-1 w-4 h-4 accent-emerald-600"
                        data-testid="contact-consent-checkbox"
                    />
                    <span className="text-sm text-stone-600">{t.contact_consent}</span>
                </label>

                {status.error && (
                    <div className="mt-5 p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="contact-error">
                        {status.error}
                    </div>
                )}
                {status.ok && (
                    <div className="mt-5 p-3 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm" data-testid="contact-success">
                        {t.contact_success}
                    </div>
                )}

                <button
                    type="submit"
                    disabled={status.loading}
                    className="mt-8 btn-primary px-10 py-4 rounded-full font-semibold disabled:opacity-60 disabled:cursor-not-allowed"
                    data-testid="contact-submit-btn"
                >
                    {status.loading ? (lang === "it" ? "Invio…" : "Sending…") : t.contact_send}
                </button>
            </form>
        </section>
    );
}
